import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { useAddProductsToProductGroup } from '@/api/api-mutations'
import { useGetProductGroup, useListProducts } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { useDebounce } from '@/utils/hooks'

export const Route = createFileRoute('/_authenticated/_general/product-groups/$groupUuid/products/add')({
	component: RouteComponent
})

function RouteComponent() {
	return (
		<RouteModal prev='..' asDrawer>
			<AddProductsForm />
		</RouteModal>
	)
}

function AddProductsForm() {
	const { L } = useI18n()
	const { groupUuid } = Route.useParams()
	const { handleSuccess } = useRouteModal()
	const group = useGetProductGroup({ groupUuid })

	const [productSearch, setProductSearch] = useState('')
	const debouncedProductSearch = useDebounce(productSearch)
	const products = useListProducts({ q: debouncedProductSearch, limit: 50 })

	const addProducts = useAddProductsToProductGroup()

	const form = useAppForm({
		defaultValues: { productUuids: [] as string[] },
		onSubmit: async ({ value }) => {
			if (value.productUuids.length === 0) {
				toast.error(L.ProductGroup.Products.Add.NoProducts())
				return
			}

			const result = await addProducts({
				path: { groupUuid },
				body: { productUuids: value.productUuids }
			})
			if (!result.isSuccess) {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
				return
			}
			toast.success(
				L.ProductGroup.Products.Add.SuccessToast({
					count: value.productUuids.length,
					name: group.data?.name ?? ''
				})
			)
			handleSuccess()
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.ProductGroup.Products.Add.Title()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='productUuids'
							children={field => (
								<field.ComboboxField
									label={L.ProductGroup.Products.Add.Label()}
									multiple
									autoFocus
									optionsLoading={products.isLoading}
									onSearchValueChange={setProductSearch}
									options={
										products.data?.products?.map(p => ({
											label: p.title,
											value: p.uuid
										})) ?? []
									}
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>

					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
